(() => {
  const initCustomerService = () => {
    const root = document.querySelector('[data-customer-service]');
    if (!root || root.dataset.initialized) return;

    const overlay = root.querySelector('[data-customer-service-close]');
    const controllers = [...root.querySelectorAll('[data-support-surface]')].map((surface) => ({
      surface,
      type: surface.dataset.supportSurface,
      toggle: surface.querySelector('[data-support-toggle]'),
      panel: surface.querySelector('[data-support-panel]'),
      content: surface.querySelector('[data-support-content]'),
    })).filter((controller) => controller.toggle && controller.panel && controller.content);
    if (!overlay || !controllers.length) return;

    root.dataset.initialized = 'true';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobileQuery = window.matchMedia('(max-width: 1024px) and (pointer: coarse)');
    let activeController = null;
    let ownsBodyLock = false;
    let mobileRevealTimer;

    const isMobileExperience = () => mobileQuery.matches
      || (navigator.maxTouchPoints > 0 && window.innerWidth <= 1024);

    const setMobileButtonsVisible = (visible, immediate = false) => {
      if (!isMobileExperience() || activeController) return;

      const surfaces = controllers.map((controller) => controller.surface);
      if (immediate || !window.gsap || reduceMotion) {
        surfaces.forEach((surface) => {
          surface.style.opacity = visible ? '1' : '0';
          surface.style.visibility = visible ? 'visible' : 'hidden';
          surface.style.pointerEvents = visible ? '' : 'none';
          surface.style.transform = visible ? '' : 'translateX(72px)';
        });
        return;
      }

      gsap.killTweensOf(surfaces);
      if (!visible) {
        surfaces.forEach((surface) => { surface.style.pointerEvents = 'none'; });
        gsap.to(surfaces, {
          x: 90,
          autoAlpha: 0,
          duration: 0.1,
          ease: 'power4.in',
          stagger: 0,
          overwrite: 'auto',
        });
        return;
      }

      surfaces.forEach((surface) => { surface.style.pointerEvents = 'none'; });
      gsap.fromTo(surfaces,
        { x: 72, autoAlpha: 0 },
        {
          x: 0,
          autoAlpha: 1,
          duration: 0.42,
          ease: 'power3.out',
          stagger: 0.08,
          overwrite: 'auto',
          onComplete: () => surfaces.forEach((surface) => { surface.style.pointerEvents = ''; }),
        });
    };

    const scheduleMobileReveal = () => {
      if (!isMobileExperience() || activeController) return;
      window.clearTimeout(mobileRevealTimer);
      mobileRevealTimer = window.setTimeout(() => setMobileButtonsVisible(true), 2000);
    };

    const resetController = (controller) => {
      controller.panel.hidden = true;
      controller.toggle.setAttribute('aria-expanded', 'false');
      controller.surface.style.width = '';
      controller.surface.style.height = '';
      controller.surface.style.borderRadius = '';
      controller.surface.style.opacity = '';
      controller.surface.style.transform = '';
      controller.surface.style.pointerEvents = '';
      controller.panel.style.opacity = '';
      controller.panel.style.visibility = '';
      controller.content.style.opacity = '';
      controller.content.style.transform = '';
      controller.toggle.style.opacity = '';
      controller.toggle.style.transform = '';
      controller.toggle.style.pointerEvents = '';
    };

    const closeActive = (immediate = false) => {
      if (!activeController) return;
      const controller = activeController;
      const closedSize = window.innerWidth <= 749 ? 52 : 56;
      activeController = null;
      root.classList.remove('is-open', 'is-whatsapp-open', 'is-contact-open');

      const finish = () => {
        resetController(controller);
        controllers.forEach((candidate) => {
          candidate.surface.style.opacity = '';
          candidate.surface.style.pointerEvents = '';
        });
        overlay.style.opacity = '';
        overlay.style.visibility = '';
        if (ownsBodyLock) {
          document.body.classList.remove('overflow-hidden');
          ownsBodyLock = false;
          window.desviewLenis?.start();
        }
        controller.toggle.focus({ preventScroll: true });
        scheduleMobileReveal();
      };

      if (immediate || !window.gsap || reduceMotion) {
        finish();
        return;
      }

      gsap.killTweensOf([controller.surface, controller.toggle, controller.panel, controller.content, overlay]);
      gsap.timeline({ onComplete: finish, defaults: { overwrite: 'auto' } })
        .to(controller.content, { autoAlpha: 0, y: 8, duration: 0.1, ease: 'power2.in' }, 0)
        .to(controller.panel, { autoAlpha: 0, duration: 0.12, ease: 'power2.in' }, 0)
        .to(controller.surface, {
          width: closedSize,
          height: closedSize,
          borderRadius: closedSize / 2,
          duration: 0.36,
          ease: 'power3.inOut',
        }, 0.08)
        .to(controller.toggle, { autoAlpha: 1, scale: 1, duration: 0.18, ease: 'power3.out' }, 0.28);
      gsap.to(overlay, { autoAlpha: 0, duration: 0.16, ease: 'power2.in' });
    };

    const openController = (controller, immediate = false) => {
      if (activeController === controller) return;
      if (activeController) closeActive(true);

      activeController = controller;
      if (!document.body.classList.contains('overflow-hidden')) {
        document.body.classList.add('overflow-hidden');
        ownsBodyLock = true;
      }
      window.desviewLenis?.stop();
      controller.panel.hidden = false;
      controller.toggle.setAttribute('aria-expanded', 'true');
      root.classList.add('is-open', `is-${controller.type}-open`);

      controllers.forEach((candidate) => {
        if (candidate === controller) return;
        candidate.surface.style.opacity = '0';
        candidate.surface.style.pointerEvents = 'none';
      });

      const targetWidth = controller.type === 'contact'
        ? Math.min(560, window.innerWidth - (window.innerWidth <= 749 ? 24 : 40))
        : Math.min(360, window.innerWidth - 32);
      const targetHeight = controller.type === 'contact'
        ? Math.min(760, window.innerHeight - (window.innerWidth <= 749 ? 24 : 40))
        : Math.min(window.innerWidth <= 749 ? 248 : 236, window.innerHeight - 96);

      const revealFocus = () => {
        controller.panel.querySelector('[data-support-close], a, button, input')?.focus({ preventScroll: true });
      };

      if (immediate || !window.gsap || reduceMotion) {
        controller.surface.style.width = `${targetWidth}px`;
        controller.surface.style.height = `${targetHeight}px`;
        controller.surface.style.borderRadius = '16px';
        controller.panel.style.opacity = '1';
        overlay.style.opacity = '1';
        controller.toggle.style.opacity = '0';
        controller.toggle.style.pointerEvents = 'none';
        revealFocus();
        return;
      }

      gsap.killTweensOf([controller.surface, controller.toggle, controller.panel, controller.content, overlay]);
      gsap.set(controller.panel, { autoAlpha: 1 });
      gsap.set(controller.content, { autoAlpha: 0, y: 10 });
      gsap.timeline({ defaults: { overwrite: 'auto' }, onComplete: revealFocus })
        .to(controller.toggle, { autoAlpha: 0, scale: 0.76, duration: 0.12, ease: 'power2.in' }, 0)
        .to(overlay, { autoAlpha: 1, duration: 0.2, ease: 'power2.out' }, 0)
        .to(controller.surface, {
          width: targetWidth,
          height: targetHeight,
          borderRadius: 16,
          duration: 0.46,
          ease: 'power3.out',
        }, 0)
        .to(controller.content, { autoAlpha: 1, y: 0, duration: 0.25, ease: 'power3.out' }, 0.26);
    };

    controllers.forEach((controller) => {
      controller.toggle.addEventListener('click', () => openController(controller));
      controller.panel.addEventListener('click', (event) => event.stopPropagation());
      controller.content.addEventListener('wheel', (event) => event.stopPropagation(), { passive: true });
      controller.content.addEventListener('touchmove', (event) => event.stopPropagation(), { passive: true });
      controller.panel.querySelector('[data-support-close]')?.addEventListener('click', () => closeActive());
    });

    overlay.addEventListener('click', () => closeActive());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeController) closeActive();
    });

    const handleMobileScroll = () => {
      if (!isMobileExperience() || activeController) return;
      window.clearTimeout(mobileRevealTimer);
      setMobileButtonsVisible(false);
      scheduleMobileReveal();
    };

    const handleMobileBreakpointChange = () => {
      if (isMobileExperience()) {
        setMobileButtonsVisible(false, true);
        scheduleMobileReveal();
      }
      else {
        window.clearTimeout(mobileRevealTimer);
        controllers.forEach((controller) => {
          controller.surface.style.opacity = '';
          controller.surface.style.visibility = '';
          controller.surface.style.pointerEvents = '';
          controller.surface.style.transform = '';
        });
      }
    };

    window.addEventListener('scroll', handleMobileScroll, { passive: true });
    mobileQuery.addEventListener?.('change', handleMobileBreakpointChange);
    handleMobileBreakpointChange();

    const contactForm = root.querySelector('.contact-service-form');
    const captchaInput = root.querySelector('[data-contact-captcha-input]');
    const captchaCode = root.querySelector('[data-contact-captcha-code]');
    const captchaRefresh = root.querySelector('[data-contact-captcha-refresh]');
    const captchaError = root.querySelector('[data-contact-captcha-error]');
    let expectedCaptcha = '';

    const generateCaptcha = () => {
      expectedCaptcha = String(Math.floor(1000 + Math.random() * 9000));
      if (captchaCode) captchaCode.textContent = expectedCaptcha;
      if (captchaInput) {
        captchaInput.value = '';
        captchaInput.removeAttribute('aria-invalid');
      }
      if (captchaError) captchaError.hidden = true;
    };

    if (contactForm && captchaInput && captchaCode) {
      generateCaptcha();
      captchaRefresh?.addEventListener('click', () => {
        generateCaptcha();
        captchaInput.focus();
      });
      captchaInput.addEventListener('input', () => {
        captchaInput.removeAttribute('aria-invalid');
        if (captchaError) captchaError.hidden = true;
      });
      contactForm.addEventListener('submit', (event) => {
        if (captchaInput.value.trim() === expectedCaptcha) return;
        event.preventDefault();
        captchaInput.setAttribute('aria-invalid', 'true');
        if (captchaError) captchaError.hidden = false;
        captchaInput.focus();
      });
    }

    if (root.querySelector('.contact-service-form__notice')) {
      const contactController = controllers.find((controller) => controller.type === 'contact');
      if (contactController) openController(contactController, true);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomerService, { once: true });
  }
  else {
    initCustomerService();
  }
})();
